import ENVS from "./envs";

export const POSTHOG_DIRECT_API_HOST = "https://us.i.posthog.com";

export const POSTHOG_DEFAULT_UI_HOST = "https://us.posthog.com";

/** Same-origin reverse proxy path served by the Vite dev proxy. Deployed builds set the API proxy. */
export const POSTHOG_FIRST_PARTY_PROXY_PATH = "/ph";

export type PosthogClientHosts = {
  apiHost: string;
  uiHost: string;
  usesReverseProxy: boolean;
};

/**
 * Hosts that ad blockers commonly drop. Includes PostHog's ingest domain and any
 * hostname/path containing "posthog" (e.g. Cloud Run `posthog-proxy-*.run.app`).
 */
export function isAdBlockVulnerablePosthogApiHost(host: string): boolean {
  const normalized = host.trim().replace(/\/+$/, "");
  if (!normalized) {
    return true;
  }
  if (normalized === POSTHOG_DIRECT_API_HOST) {
    return true;
  }
  if (normalized === POSTHOG_FIRST_PARTY_PROXY_PATH) {
    return false;
  }

  try {
    const url = new URL(normalized, "http://localhost");
    if (url.hostname.toLowerCase().includes("posthog")) {
      return true;
    }
  } catch {
    if (normalized.toLowerCase().includes("posthog")) {
      return true;
    }
  }

  return false;
}

function readConfiguredPosthogApiHost(): string {
  const configured = ENVS.posthogApiHost?.trim().replace(/\/+$/, "") ?? "";
  if (configured.length === 0 || isAdBlockVulnerablePosthogApiHost(configured)) {
    return "";
  }
  return configured;
}

export function resolvePosthogClientHosts(): PosthogClientHosts {
  const configuredApiHost = readConfiguredPosthogApiHost();
  const apiHost = configuredApiHost.length > 0 ? configuredApiHost : POSTHOG_FIRST_PARTY_PROXY_PATH;

  const uiHost = ENVS.posthogUiHost?.trim().replace(/\/+$/, "") || POSTHOG_DEFAULT_UI_HOST;

  return {
    apiHost,
    uiHost,
    usesReverseProxy: apiHost !== POSTHOG_DIRECT_API_HOST,
  };
}

/** The `conversations` block of PostHog's remote config, or false when Support is off. */
export type PosthogConversationsRemoteConfig =
  | false
  | {
      enabled?: boolean;
      widgetEnabled?: boolean;
      domains?: string[];
      token?: string;
    };

/**
 * Reads the project's Support (conversations) settings the same way the SDK does at init.
 * A failed request throws, so callers never mistake an outage for Support being off.
 */
export const fetchPosthogConversationsRemoteConfig = async (
  signal?: AbortSignal
): Promise<PosthogConversationsRemoteConfig> => {
  const key = ENVS.posthogKey?.trim();
  if (!key) {
    return false;
  }

  const { apiHost } = resolvePosthogClientHosts();
  const response = await fetch(`${apiHost}/flags/?v=2&config=true`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      api_key: key,
      distinct_id: "rivetai-support-diagnostics",
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`PostHog remote config request failed (${response.status})`);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error(`PostHog proxy at ${apiHost} returned non-JSON (${contentType || "unknown"})`);
  }

  const data = (await response.json()) as { conversations?: PosthogConversationsRemoteConfig };
  return data.conversations ?? false;
};
